<script lang="ts" setup>
  import { type PropType, watch } from 'vue';
  import type { Breadcrumb, Section, Module, Brand } from '@storefront/types';
  import { viewItemListEvent, selectContentEvent, useGoogleTagManager } from '@storefront/storefront';
  import { useShared } from '@othercommerce/foundation';
  import type { ProductListingResource } from '@storefront/resources';


  const props = defineProps({
    brand: { type: Object as PropType<Brand>, required: true },
    module: { type: Object as PropType<Module>, required: true },
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: true },
    sections: { type: Array as PropType<Section[]>, required: true },
    brands: { type: Array as PropType<Brand[]>, required: true },
    products: { type: Object as PropType<ProductListingResource[]>, required: true },
    productsCount: { type: Number, required: true },
  });

  const shared = useShared();
  const gtm = useGoogleTagManager();

  watch(
    () => props.products,
    (products) => {
      gtm.push(viewItemListEvent(
        products,
        shared.value.activeCurrencyCode,
      ));
    },
    {
      immediate: true,
    },
  );

  function onBrandSelect(brand: Brand) {
    gtm.push(selectContentEvent('brand', brand.name));
  }

</script>
<template>
  <main id="main" class="container brand-site">
    <SfBreadcrumbs :breadcrumbs="breadcrumbs" />
    <SectionRenderer :sections="sections" placement="before" />
    <div class="brands">
      <SfTile class="brands__content">
        <template #title>
          <SfHeading type="h1">
            {{ brand.name }}
          </SfHeading>
        </template>
        <template #default>
          <p v-if="brand.description" v-html="brand.description" class="brands__description" />
          <div v-if="products.length > 0" class="brands__products">
            <ProductCard v-for="product in products" :product horizontal />
            <div class="brands__show">
              <SfButton :href="$route('web.product.index', { brands: [brand.id] })" primary>
                {{ $t('SHOP.BRAND.ACTION.SEE_ALL_RELATED_PRODUCTS') }}
                <span v-if="productsCount > products.length">({{ productsCount }})</span>
              </SfButton>
            </div>
          </div>
          <SfDataPlaceholder v-else class="brands__empty">
            {{ $t('SHOP.BRAND.MESSAGE.NO_PRODUCTS') }}
          </SfDataPlaceholder>
        </template>
      </SfTile>
      <div class="brands__list">
        <SfHeading type="h2">
          {{ $t('SHOP.BRAND.HEADER.ALL_BRANDS') }}
        </SfHeading>
        <div class="brands__grid">
          <SfTile v-for="brand in brands" @click="onBrandSelect(brand)" :href="$route('web.brand.show', { brand: brand })" class="brands__brand" interactive shadow small>
            <MediaPicture :photo="brand.photo" sizes="200x150 x2, 100x75" type="logo" />
            <span class="brands__name">{{ brand.name }}</span>
          </SfTile>
        </div>
      </div>
    </div>
    <SectionRenderer :sections placement="after" />
  </main>

  <RouterView />
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .brand-site {
    display: flex;
    flex-direction: column;
    gap: var(--sf-section-renderer-gap);
  }

  .brands {
    display: flex;
    flex-direction: row-reverse;
    align-items: flex-start;
    gap: 2rem;

    &__empty {
      margin-top: 2rem;
    }

    &__description {
      font-size: 0.875rem;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      flex: 0 0 280px;
      font-size: 0.875rem;
    }

    &__grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    &__brand {
      box-shadow: var(--sf-brands-list-shadow);


      :deep(.tile__content) {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        padding: 0.25rem 0.5rem;
      }

      :deep(picture) {
        display: contents;

        source {
          display: contents;
        }

        img {
          flex: 0 0 60px;
          width: 60px;
          height: 40px;
        }
      }

      &.active {
        border: var(--sf-brands-list-active-border);
        box-shadow: var(--sf-brands-list-active-shadow);
        transform: var(--sf-brands-list-active-transform);

        .brands__name {
          color: var(--sf-brands-list-active-color);
          font-weight: var(--font-weight-bold);
        }
      }
    }

    &__name {
      display: flex;
      font-weight: var(--font-weight-semi);
    }

    &__content {
      width: 100%;

      :deep(.tile__content) {
        justify-content: flex-start;
        align-items: flex-start;
      }
    }

    &__products {
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
      width: 100%;
    }

    &__show {
      display: flex;
      justify-content: center;
      margin-top: 2rem;
    }
  }

  @include media.query(lg) {
    .brands {
      flex-direction: column;
      padding: 0;
      margin-bottom: 2rem;
      --sf-tile-small-gap: 0.5rem;

      &__content > :deep(.tile__content) {
        padding: 1rem 0.625rem;
      }

      &__list {
        flex: auto;
        width: 100%;
        padding: 0 0.625rem;
      }

      &__grid {
        grid-template-columns: 1fr 1fr 1fr;
      }

      &__brand :deep(.tile__content) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }

      &__name {
        text-align: center;
        font-size: 0.75rem;
      }

      &__show {
        margin-top: 0;
      }
    }
  }

  @include media.query(sm) {
    .brands__grid {
      grid-template-columns: 1fr 1fr;
    }
  }
</style>
